import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { IoTrash } from 'react-icons/io5';
import styles from './agency-products-table.module.scss';
import type { IAgencyProduct } from '~/types/agencies';

type AgencyProductPriceKey =
  | 'pricePerM2'
  | 'flatPrice'
  | 'pricePerLinearMeter'
  | 'pricePerTon'
  | 'pricePerUnit'
  | 'prestationPricePerM2'
  | 'prestationFlatPrice'
  | 'prestationPricePerUnit';

type UpdateArgs = {
  productId: string;
  key: AgencyProductPriceKey;
  value: number;
};

const PRICE_COLUMNS: { key: AgencyProductPriceKey; label: string }[] = [
  { key: 'pricePerM2', label: 'Location € / m2' },
  { key: 'flatPrice', label: 'Location Forfait' },
  { key: 'pricePerLinearMeter', label: 'Location € / m lin.' },
  { key: 'pricePerTon', label: 'Location € / tonne' },
  { key: 'pricePerUnit', label: 'Location € / unité' },
  { key: 'prestationPricePerM2', label: 'Prestation € / m2' },
  { key: 'prestationFlatPrice', label: 'Prestation Forfait' },
  { key: 'prestationPricePerUnit', label: 'Prestation € / unité' },
];

const AgencyProductsTable = (
  {
    rows,
    onUpdate,
    onRemove,
  }: {
    rows: IAgencyProduct[];
    onUpdate: (args: UpdateArgs) => void;
    onRemove: (productId: string) => void;
  },
) => {
  const { register, getValues, reset } = useForm<any>({
    defaultValues: {
      products: rows,
    },
  });

  useEffect(() => {
    reset({ products: rows });
  }, [rows, reset]);

  const onBlur = (formKey: string, productId: string, key: AgencyProductPriceKey) => {
    const value = Number(getValues(formKey as any) || 0);
    onUpdate({ productId, key, value });
  };

  return (
    <div className={styles.pricingTable}>
      <table>
        <thead>
          <tr>
            <th><p>Produit</p></th>
            {PRICE_COLUMNS.map((col) => (
              <th key={col.key}><p>{col.label}</p></th>
            ))}
            <th className={styles.colActions} />
          </tr>
        </thead>
        <tbody>
          {(rows || []).map((row, index) => {
            const productTitle = (row as any)?.product?.title || (row as any)?.productId || '-';
            const productId = (row as any)?.productId || (row as any)?.product?._id;

            return (
              <tr key={`agency-product-${productId}`}>
                <td>
                  <p className={styles.location} title={productTitle}>
                    {productTitle}
                  </p>
                </td>
                {PRICE_COLUMNS.map((col) => {
                  const formKey = `products.${index}.${col.key}`;
                  const reg = register(formKey as any);

                  return (
                    <td key={col.key} className={styles.containerInput}>
                      <input
                        type='number'
                        step='0.01'
                        defaultValue={(row as any)?.[col.key] ?? 0}
                        {...reg}
                        onBlur={(e) => {
                          const { onBlur: fieldOnBlur } = reg;
                          fieldOnBlur(e);
                          onBlur(formKey, productId, col.key);
                        }}
                      />
                    </td>
                  );
                })}
                <td className={`${styles.actions} ${styles.colActions}`}>
                  <button
                    type='button'
                    className={styles.trashButton}
                    onClick={() => onRemove(productId)}
                    aria-label="Retirer le produit de l'agence"
                  >
                    {IoTrash({ size: 20 })}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default AgencyProductsTable;
